import axios, { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';

import { Scenario, Structure } from 'models';

type ForecastQuery = {
    targetTime: number
    structure: Structure
    targetValue: number
    volumeOfConcrete: number
    surfaceOfFormwork: number
    carbonFootprintElectricity: number
    costsOfElectricity: number
    scenarios: Scenario[]
}

type ScenariosModel = {
    targetTime: number
    structureId: number
    targetValue: number
    volumeOfConcrete: number
    surfaceOfFormwork: number
    carbonFootprintElectricity: number
    costsOfElectricity: number
    scenarios: {
        id: number
        ambientTemperature: number
        freshConcreteTemperature: number
        concreteMixtureId: number
        calibrationCurveId: number
        priceOfConcrete: number
        recommendedTemperature?: number
        concreteCarbonFootprint?: number
        concreteCosts?: number
        electricityConsumption?: number
        electricityCarbonFootprint?: number
        electricityCosts?: number
        totalCarbonFootprint?: number
        totalCosts?: number
    }[]
}

type ScenariosResponse = {
    data: ScenariosModel
}

type useForecastResult = {
    calculate: (query: ForecastQuery) => Promise<Scenario[]>
    isLoading: boolean
    errorMessage: string | null
};

const calculateScenarios = async (data: ScenariosModel) => {
    const url = `${import.meta.env.VITE_APP_API_URL}/scenarios`;
    const response = await axios.post<ScenariosResponse>(url, data as ScenariosModel);
    return response.data;
};


export const useForecast = (): useForecastResult => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const calculate = async (query: ForecastQuery) => {
        try {

            setIsLoading(true);
            const calculatedScenarios = await calculateScenarios({
                ...query,
                structureId: query.structure.id,
                scenarios: query.scenarios.map(scenario => ({
                    ...scenario,
                    concreteMixtureId: scenario.concreteMixture!.id,
                    calibrationCurveId: scenario.calibrationCurve!.id,
                    priceOfConcrete: scenario.concretePrice
                }))
            });

            const scenarios: Scenario[] = query.scenarios.map(scenario => {
                const calculatedScenario = calculatedScenarios.data.scenarios.find(calculatedScenario => calculatedScenario.id === scenario.id);

                return {
                    ...scenario,
                    forecast: {
                        recommendedTemperature: calculatedScenario?.recommendedTemperature || 0,
                        electricityConsumption: calculatedScenario?.electricityConsumption || 0,
                        co2Electricity: calculatedScenario?.electricityCarbonFootprint || 0,
                        electricityCosts: calculatedScenario?.electricityCosts || 0,
                        co2Concrete: calculatedScenario?.concreteCarbonFootprint || 0,
                        concreteCosts: calculatedScenario?.concreteCosts || 0
                    },
                    summary: {
                        co2Total: calculatedScenario?.totalCarbonFootprint || 0,
                        co2Recommended: false,
                        costsTotal: calculatedScenario?.totalCosts || 0,
                        costsRecommended: false
                    }
                };
            });

            // Set recommendations
            if (scenarios.length > 1) {
                const co2Minimum = Math.min(...scenarios.map(scenario => scenario.summary!.co2Total));
                const costsMinimum = Math.min(...scenarios.map(scenario => scenario.summary!.costsTotal));

                scenarios.forEach(scenario => {
                    scenario.summary!.co2Recommended = (scenario.summary!.co2Total === co2Minimum);
                    scenario.summary!.costsRecommended = (scenario.summary!.costsTotal === costsMinimum);
                });
            };

            setErrorMessage(null);
            return scenarios;
        } catch (error) {
            if (error) {
                setErrorMessage((isAxiosError(error) ? error.response?.data.detail || error.message : t('errorCalculatingForecast')));
            }
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { calculate, isLoading, errorMessage };
};
